import { FC, useState } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { LabeledInput } from "components/inputs/LabeledInput"
import { ToggleCard } from "components/inputs/ToggleCard"
import { ModalConfirmDestructive } from "components/modals/ModalConfirmDestructive"
import { SectionCard } from "components/settings/SectionCard"

import { ConnectionStatusBar, ConnectionStatusBarProps } from "./ConnectionStatusBar"
import { FormState } from "./types"

interface CredentialsSectionProps extends ConnectionStatusBarProps {
  form: FormState
  saving: boolean
  deleting: boolean
  onFieldChange: <K extends keyof Pick<FormState, "enabled" | "siteId" | "apiKey" | "appApiKey" | "region" | "amountFormat">>(
    key: K,
    value: FormState[K]
  ) => void
  onDeleteCredentials: () => void
}

export const CredentialsSection: FC<CredentialsSectionProps> = ({
  form,
  saving,
  deleting,
  onFieldChange,
  onDeleteCredentials,
  status,
  onTest,
  testing
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <SectionCard title={t("settings.customerio.credentials.title")}>
      <ConnectionStatusBar status={status} onTest={onTest} testing={testing} />

      <ToggleCard
        label={t("settings.customerio.credentials.enable")}
        checked={form.enabled}
        onChange={() => onFieldChange("enabled", !form.enabled)}
        disabled={saving}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        <LabeledInput
          id="cio-site-id"
          label={t("settings.customerio.credentials.siteId")}
          value={form.siteId}
          onChange={(v) => onFieldChange("siteId", v)}
          disabled={saving}
          placeholder={t("settings.customerio.credentials.siteIdPlaceholder")}
        />

        <LabeledInput
          id="cio-api-key"
          label={t("settings.customerio.credentials.apiKey")}
          value={form.apiKey}
          onChange={(v) => onFieldChange("apiKey", v)}
          disabled={saving}
          revealable
          autoComplete="one-time-code"
          placeholder={t("settings.customerio.credentials.notSet")}
        />

        <LabeledInput
          id="cio-app-api-key"
          label={t("settings.customerio.credentials.appApiKey")}
          value={form.appApiKey}
          onChange={(v) => onFieldChange("appApiKey", v)}
          disabled={saving}
          revealable
          autoComplete="one-time-code"
          placeholder={t("settings.customerio.credentials.notSet")}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DropdownMenu
          title={t("settings.customerio.credentials.region")}
          options={[
            { name: t("settings.customerio.credentials.regionEu"), value: "EU" },
            { name: t("settings.customerio.credentials.regionUs"), value: "US" }
          ]}
          value={form.region}
          onChange={(e: { target: { value: string } }) => onFieldChange("region", e.target.value as FormState["region"])}
          hideEmptyOption
          disableEmptyOption
          disabled={saving}
        />
        <DropdownMenu
          title={t("settings.customerio.credentials.amountFormat")}
          options={[
            { name: t("settings.customerio.credentials.amountFormatted"), value: "formatted" },
            { name: t("settings.customerio.credentials.amountRaw"), value: "raw" }
          ]}
          value={form.amountFormat}
          onChange={(e: { target: { value: string } }) => onFieldChange("amountFormat", e.target.value as FormState["amountFormat"])}
          hideEmptyOption
          disableEmptyOption
          disabled={saving}
        />
      </div>

      <div className="flex justify-end pt-2 border-t border-black/10 dark:border-white/10">
        <Button variant="error" size="sm" className="btn-outline" onClick={() => setConfirmDelete(true)} disabled={saving} loading={deleting}>
          {t("settings.customerio.deleteCredentials")}
        </Button>
      </div>

      <ModalConfirmDestructive
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          onDeleteCredentials()
          setConfirmDelete(false)
        }}
        loading={deleting}
        title={t("settings.customerio.deleteCredentials")}
        description={t("settings.customerio.deleteCredentialsConfirm")}
        consequences={[
          t("settings.customerio.deleteConsequenceKeys"),
          t("settings.customerio.deleteConsequenceProvider"),
          t("settings.customerio.deleteConsequenceTemplates")
        ]}
        confirmLabel={t("settings.customerio.deleteCredentialsAction")}
      />
    </SectionCard>
  )
}
